import type { MutableRefObject } from 'react';
import React, { useState } from 'react';
import type { Nullable } from 'corgi-types';
import type { SpaceProps, WidthProps } from 'styled-system';

import { MessageType } from '../../enums/Alert';
import Alert from '../Alert';
import { useScrollToTarget } from './scroll-to-target';
import { useTrackImpression } from './use-track-impression';

interface AutoScrollAlertProps extends SpaceProps, WidthProps {
  message: string;
  messageId: number;
  scrollOnError?: boolean;
  trackImpression?: boolean;
  containerRef?: MutableRefObject<Nullable<HTMLElement>>;
  type?: MessageType;
  onClick?: () => void;
  shouldScrollIntoView?: boolean;
}

const SCROLL_OPTIONS = {
  verticalPadding: 16,
  duration: 500,
};

const AutoScrollAlert = ({
  message,
  scrollOnError = true,
  trackImpression = true,
  messageId,
  containerRef,
  type = MessageType.Error,
  onClick,
  shouldScrollIntoView,
  ...spaceProps
}: AutoScrollAlertProps) => {
  const [elementToScrollTo, setElementToScrollTo] = useState<Nullable<HTMLElement>>(null);

  const onElementMount = useTrackImpression({
    trackImpression,
    element: elementToScrollTo,
    message,
    messageId,
  });

  useScrollToTarget({
    ...SCROLL_OPTIONS,
    containerRef,
    element: elementToScrollTo,
    scrollOnError,
    messageId,
    shouldScrollIntoView,
  });

  const setAlertRef = (element: Nullable<HTMLDivElement>) => {
    if (element) {
      onElementMount(element);
    }
    setElementToScrollTo(element);
  };

  return (
    <Alert
      mb="sp_16"
      type={type}
      html={message}
      ref={setAlertRef}
      onClick={onClick}
      {...spaceProps}
    />
  );
};

export default AutoScrollAlert;
